import MainContainer from "components/MainContainer";
import { setSelectedSubscription } from "features/slices/reservationFlowSlice";
import { SubscriptionDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useFetchTenantHasTflProviderQuery } from "queries/ticket.queries";
import React, { useEffect, useMemo, useState } from "react";
import { Pressable, View } from "react-native";
import {
  getUniqSubscriptions,
  isVisibleToMembers,
  sortVoucherSubsByUnusedVouchers,
} from "utils/subscriptionUtils";
import CapturedSchedule from "./CapturedSchedule";
import TflEventSearch from "./TflEventSearch";
import PlanPackageSelector from "./components/PlanPackageSelector";
import Spacer from "components/Spacer";
import { Text } from "@ui-kitten/components";

export default function Schedule() {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState("");
  const { account } = useAccount();
  if (!account) return null;

  const { data: hasTflProvider } = useFetchTenantHasTflProviderQuery();
  const { selectedSubscription } = useAppSelector(
    (state) => state.reservationFlow
  );

  const uniqSubscriptions = useMemo(() => {
    return getUniqSubscriptions(account.subscriptions)
      .filter(isVisibleToMembers)
      .sort((a, b) => a.id - b.id) // sort subscriptions by id, looks hierarchically especially when there are child plans
      .sort(
        // https://github.com/FanRally/fanrally-core/issues/1271
        sortVoucherSubsByUnusedVouchers
      );
  }, [account.subscriptions]);

  const tabs = useMemo(() => {
    const result: { key: string; title: string }[] = [];
    if (!selectedSubscription) return result;

    if (hasTflProvider && selectedSubscription.planPackage.tflTabEnabled) {
      const tflTabName =
        selectedSubscription.planPackage.tflTabName || "Tickets";
      result.push({ key: "TFL Tab", title: tflTabName });
    }

    const planTabName = selectedSubscription.planPackage.tabName || "Top Picks";
    result.push({ key: "Plan Tab", title: planTabName });

    return result;
  }, [hasTflProvider, selectedSubscription]);

  useEffect(() => {
    if (tabs.length === 0) return;
    setActiveTab(tabs[0].key);
  }, [tabs]);

  // gets called when initial mount and user adds/upgrades another membership
  useEffect(() => {
    if (selectedSubscription === null) {
      dispatch(setSelectedSubscription(uniqSubscriptions[0]));
    }
  }, [account.subscriptions]);

  return (
    <MainContainer title="Schedule">
      {uniqSubscriptions.length === 0 && (
        <Text style={{ textAlign: "center", marginTop: 5 }} category="h6">
          No subscriptions found
        </Text>
      )}
      <View className="p-2">
        {uniqSubscriptions.length > 1 && (
          <Spacer>
            <PlanPackageSelector
              selectedSubscription={selectedSubscription}
              uniqSubscriptions={uniqSubscriptions}
              onSelect={(selectedSubscription: SubscriptionDetails) =>
                dispatch(setSelectedSubscription(selectedSubscription))
              }
            />
          </Spacer>
        )}

        {tabs.length > 1 && (
          <View className="flex-row justify-around bg-white border-b border-gray-200">
            {tabs.map((tab) => (
              <Pressable
                key={tab.key}
                onPress={() => setActiveTab(tab.key)}
                style={[
                  {
                    paddingVertical: 12,
                    paddingHorizontal: 16,
                  },
                  activeTab === tab.key && {
                    borderBottomWidth: 2,
                    borderBottomColor: "#ccc",
                  },
                ]}
              >
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: "bold",
                    color: activeTab === tab.key ? "black" : "gray",
                  }}
                >
                  {tab.title}
                </Text>
              </Pressable>
            ))}
          </View>
        )}

        <View className="flex-1">
          {tabs.map((tab) => (
            <View
              className={`flex-1 ${activeTab === tab.key ? "flex" : "hidden"}`}
              key={tab.key}
            >
              {tab.key === "TFL Tab" && hasTflProvider && <TflEventSearch />}
              {tab.key === "Plan Tab" && <CapturedSchedule />}
            </View>
          ))}
        </View>
      </View>
    </MainContainer>
  );
}
